import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OptimizedImg from "../../components/OptimizedImg";

import ProjectOneProductDetailPage from "./ProjectOneProductDetailPage";
import ProjectTwoProductDetailPage from "./ProjectTwoProductDetailPage";
import ProjectThreeProductDetailPage from "./ProjectThreeProductDetailPage";
import AGCorporationProductPage from "./AGCorporationProductPage";
import SagveekLMSProductPage from "./SagveekLMSProductPage";

/* =====================================================================
   DESIGN TOKENS
   Matched 1:1 with the Sagveek product ecosystem (#6C0A9F theme).
===================================================================== */

const C = {
  bg: "#FAF8FC",
  surface: "#FFFFFF",
  raised: "#F5EEFA",
  border: "#E6DCEF",
  muted: "#D3B9E3",
  dimText: "#8A7B96",
  subText: "#5A4E68",
  text: "#2A2033",
  bright: "#170B2E",
  teal: "#3D1259",
  tealD: "#12061F",
  tealGlow: "#3D125914",
  coral: "#6C0A9F",
  amber: "#8A14C9",
  violet: "#A855F7",
};

const FF = {
  display: "'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

/* Reusable centered eyebrow label */
function Label({ text, light = false }) {
  const color = light ? "#fff" : C.coral;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 8,
        fontFamily: FF.body,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: "1.5px",
        color,
        textTransform: "uppercase",
        marginBottom: 14,
      }}
    >
      <div style={{ width: 16, height: 1.5, background: color }} />
      {text}
      <div style={{ width: 16, height: 1.5, background: color }} />
    </div>
  );
}

/* =====================================================================
   HOOKS
===================================================================== */

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/* =====================================================================
   PRODUCT DATA
===================================================================== */

export const ProductsData = [
  {
    slug: "rent-property-management-solution",
    DetailComponent: ProjectThreeProductDetailPage,
    index: "01",
    eyebrowLabel: "Property Tech",
    sector: "Real Estate",
    productName: "Rent & Property Management",
    tagline: "One platform for listings, agreements, and leads.",
    accentColor: "#6C0A9F",
    bgColor: C.raised,
    images: [
      "/images/samone/1.png",
      "/images/samone/2.png",
      "/images/samone/3.png",
      "/images/samone/4.png",
    ],
    features: [
      "OTP-verified tenant & owner registration",
      "Digital agreement uploads & e-sign",
      "Property listings & search portal",
      "Lead tracking & enquiry management",
    ],
    stats: [
      { value: 200, decimals: 0, suffix: "+", label: "Clients" },
      { value: 99.7, decimals: 1, suffix: "%", label: "Uptime" },
      { value: 12, decimals: 0, suffix: " hrs/week", label: "Time Saved" },
    ],
  },
  {
    slug: "hospital-equipment-management",
    DetailComponent: ProjectTwoProductDetailPage,
    index: "02",
    eyebrowLabel: "Asset Management",
    sector: "Healthcare",
    productName: "Medical Equipment Management",
    tagline: "Audit-ready asset control across every department.",
    accentColor: "#6C0A9F",
    bgColor: C.raised,
    images: [
      "/images/project2/0.png",
      "/images/project2/1.png",
      "/images/project2/2.png",
      "/images/project2/3.png",
      "/images/project2/4.png",
    ],
    features: [
      "Equipment lifecycle & maintenance tracking",
      "Calibration records & compliance logs",
      "Department-wise asset allocation",
      "Automated service reminders & alerts",
    ],
    stats: [
      { value: 85, decimals: 0, suffix: "+", label: "Clients" },
      { value: 99.95, decimals: 2, suffix: "%", label: "Uptime" },
      { value: 40, decimals: 0, suffix: " hrs/month", label: "Time Saved" },
    ],
  },
  {
    slug: "printing-management-system",
    DetailComponent: ProjectOneProductDetailPage,
    index: "03",
    eyebrowLabel: "Operations",
    sector: "Print Operations",
    productName: "Printing Management System",
    tagline: "End-to-end control from order intake to delivery.",
    accentColor: "#6C0A9F",
    bgColor: C.raised,
    images: [
      "/images/project3/1.png",
      "/images/project3/2.png",
      "/images/project3/3.png",
      "/images/project3/4.png",
    ],
    features: [
      "Real-time job tracking & status updates",
      "Automated order intake & billing",
      "Inventory & consumables management",
      "Customer CRM with order history",
    ],
    stats: [
      { value: 120, decimals: 0, suffix: "+", label: "Clients" },
      { value: 99.8, decimals: 1, suffix: "%", label: "Uptime" },
      { value: 6, decimals: 0, suffix: " hrs/day", label: "Time Saved" },
    ],
  },
  {
    slug: "electrical-panel-management",
    DetailComponent: AGCorporationProductPage,
    index: "04",
    eyebrowLabel: "Industrial Tech",
    sector: "MEP & Electrical",
    productName: "Electrical Panel Management",
    tagline: "Every panel, wired for visibility across manufacturing and field warranty.",
    accentColor: "#6C0A9F",
    bgColor: C.raised,
    images: [
      "/images/project4/1.png",
      "/images/project4/2.png",
      "/images/project4/3.png",
      "/images/project4/4.png",
      "/images/project4/5.png",
    ],
    features: [
      "QR code tracking linked to live panel records",
      "Protection settings captured at commissioning",
      "Multi-site warranty & lifecycle logs",
      "Real-time reports with CSV/Excel exports",
    ],
    stats: [
      { value: 600, decimals: 0, suffix: "+", label: "Panels Live" },
      { value: 40, decimals: 0, suffix: "%", label: "Faster Commissioning" },
      { value: 64, decimals: 0, suffix: "+", label: "Site Engineers" },
    ],
  },
  {
    slug: "learning-management-system",
    DetailComponent: SagveekLMSProductPage,
    index: "05",
    eyebrowLabel: "EdTech",
    sector: "Education & Training",
    productName: "Sagveek Learning Management",
    tagline: "Enroll with a scan. Finish with a certificate.",
    accentColor: "#6C0A9F",
    bgColor: C.raised,
    images: ["/images/project5/1.png", "/images/project5/2.png"],
    features: [
      "QR-code enrollment, instant sign-in",
      "Video lessons with resumable progress",
      "Auto-graded quizzes with instant scoring",
      "Certificates issued automatically",
    ],
    stats: [
      { value: 120, decimals: 0, suffix: "+", label: "Courses Live" },
      { value: 8, decimals: 0, suffix: "k+", label: "Learners" },
      { value: 35, decimals: 0, suffix: "%", label: "Faster Onboarding" },
    ],
  },
];

const globalStats = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="8" r="3.2" stroke="#fff" strokeWidth="1.6" />
        <path d="M3.5 20c0-3.3 2.5-5.8 5.5-5.8s5.5 2.5 5.5 5.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M15.5 8.6a3 3 0 1 1 2.9 3.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M16.5 14.4c2.6.3 4.5 2.5 4.5 5.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    value: 1500,
    decimals: 0,
    suffix: "+",
    label: "Happy Customers",
    bg: "#6C0A9F",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1z" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14 3.5V8h4" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M8.5 12.5l2 2 4-4.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    value: 2500,
    decimals: 0,
    suffix: "+",
    label: "Projects Delivered",
    bg: "#8A14C9",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="8.2" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="4.6" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="1.2" fill="#fff" />
      </svg>
    ),
    value: 98,
    decimals: 0,
    suffix: "%",
    label: "Client Satisfaction",
    bg: "#6C0A9F",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4.5 13.5V12a7.5 7.5 0 0 1 15 0v1.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="3" y="13" width="4" height="6" rx="1.4" stroke="#fff" strokeWidth="1.6" />
        <rect x="17" y="13" width="4" height="6" rx="1.4" stroke="#fff" strokeWidth="1.6" />
        <path d="M19 19.5c0 1.4-1.6 2.5-4 2.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    value: 24,
    decimals: 0,
    suffix: "/7",
    label: "Expert Support",
    bg: "#A855F7",
  },
];

const whyChooseUs = [
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M18 4L6 9v9c0 7.7 5.1 14.9 12 16.7C25.9 32.9 31 25.7 31 18V9L18 4z" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M13 18l3 3 7-7" stroke="#6C0A9F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: "Secure & Reliable",
    desc: "Enterprise-grade security to protect your data and ensure reliability.",
    accent: "#6C0A9F",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M20 4L10 20h8l-2 12 10-16h-8l2-12z" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: "Easy to Use",
    desc: "Intuitive interfaces that anyone can use with ease.",
    accent: "#6C0A9F",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <rect x="6" y="22" width="6" height="10" rx="2" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" />
        <rect x="15" y="16" width="6" height="16" rx="2" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" />
        <rect x="24" y="10" width="6" height="22" rx="2" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" />
        <path d="M8 20L15 13L22 17L30 10" stroke="#6C0A9F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    title: "Scalable Solutions",
    desc: "Built to grow with your business, from startup to enterprise.",
    accent: "#6C0A9F",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="13" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" />
        <path d="M14 14c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4" stroke="#6C0A9F" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="18" cy="25" r="1.5" fill="#6C0A9F" />
        <path d="M12 28c1.4-1.2 3.4-2 6-2s4.6.8 6 2" stroke="#6C0A9F" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    title: "Expert Support",
    desc: "Our team is always here to help you succeed.",
    accent: "#6C0A9F",
  },
  {
    icon: (
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        <path d="M18 8c-5.5 0-10 3.1-10 7 0 2.2 1.4 4.1 3.6 5.5L10 26l6-3c.6.1 1.3.1 2 .1 5.5 0 10-3.1 10-7S23.5 8 18 8z" fill={C.raised} stroke="#6C0A9F" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M18 12c3.3 0 6 1.8 6 4" stroke="#6C0A9F" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
    title: "Cloud Ready",
    desc: "Access your data anytime, anywhere with our cloud solutions.",
    accent: "#6C0A9F",
  },
];

/* =====================================================================
   SCREENSHOT / IMAGE CAROUSEL
===================================================================== */

const ScreenshotMockup = ({ type, accent, bg }) => {
  const common = { viewBox: "0 0 400 250", xmlns: "http://www.w3.org/2000/svg", style: { width: "100%", height: "100%", display: "block" } };
  return (
    <svg {...common}>
      <rect width="400" height="250" fill={bg} />
      <rect x="0" y="0" width="88" height="250" fill="#fff" opacity="0.6" />
      {[30, 62, 94, 126].map((y, i) => (
        <rect key={i} x="18" y={y} width="52" height="10" rx="5" fill={accent} opacity={i === 0 ? 0.6 : 0.25} />
      ))}
      <rect x="108" y="24" width="270" height="40" rx="8" fill="#fff" opacity="0.75" />
      <rect x="124" y="38" width="120" height="10" rx="5" fill={accent} opacity="0.4" />
      <rect x="108" y="76" width="128" height="70" rx="8" fill={accent} opacity="0.15" />
      <rect x="244" y="76" width="134" height="70" rx="8" fill={accent} opacity="0.28" />
      <rect x="108" y="158" width="270" height="70" rx="8" fill="#fff" opacity="0.75" />
    </svg>
  );
};

const isImagePath = (src) => typeof src === "string" && /\.(png|jpe?g|webp|avif|svg|gif)$/i.test(src);

const CarouselSlide = ({ src, accent, bg, loadSrc = true, loading = "lazy" }) => {
  const [status, setStatus] = useState("loading");

  if (!isImagePath(src)) {
    return <ScreenshotMockup type={src} accent={accent} bg={bg} />;
  }

  if (!loadSrc) {
    return <div style={{ width: "100%", height: "100%", background: bg }} />;
  }

  if (status === "error") {
    return <ScreenshotMockup type="dashboard" accent={accent} bg={bg} />;
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: bg }}>
      {status === "loading" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: C.dimText,
            fontWeight: 600,
          }}
        >
          Loading…
        </div>
      )}
      <OptimizedImg
        src={src}
        alt=""
        loading={loading}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        pictureStyle={{ width: "100%", height: "100%", display: "block" }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: status === "loaded" ? "block" : "none",
        }}
      />
    </div>
  );
};

/* =====================================================================
   ANIMATED STAT
===================================================================== */

const AnimatedStat = ({ value, decimals = 0, prefix = "", suffix = "", label, color, size = 22, labelColor = C.dimText }) => {
  const [ref, inView] = useInView(0.5);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    let start = null;
    let frame;
    const duration = 1400;

    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) frame = requestAnimationFrame(step);
      else setDisplay(value);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);

  return (
    <div ref={ref} style={{ minWidth: 92 }}>
      <div style={{ fontFamily: FF.mono, fontWeight: 600, fontSize: size, color }}>
        {prefix}
        {display.toFixed(decimals)}
        {suffix}
      </div>
      <div style={{ fontFamily: FF.body, fontSize: 12, color: labelColor, fontWeight: 500, marginTop: 2 }}>{label}</div>
    </div>
  );
};

/* =====================================================================
   PROJECT CAROUSEL
===================================================================== */

const ProjectCarousel = ({ images, accent, bg, allowLoad = true }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const count = images.length;

  useEffect(() => {
    if (paused || count <= 1) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 3000);
    return () => clearInterval(id);
  }, [paused, count]);

  const goTo = (i) => setIndex(((i % count) + count) % count);
  const prev = () => goTo(index - 1);
  const next = () => goTo(index + 1);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 40) prev();
    else if (delta < -40) next();
    touchStartX.current = null;
  };

  const stackPosition = (i) => (i - index + count) % count;

  const cardStyle = (depth) => {
    const visibleDepth = Math.min(depth, 4);
    const fanDir = depth % 2 === 0 ? 1 : -1;
    const translateX = depth === 0 ? 0 : fanDir * (10 + visibleDepth * 6);
    const translateY = visibleDepth * 9;
    const rotate = depth === 0 ? 0 : fanDir * (3 + visibleDepth * 2.5);
    const scale = 1 - visibleDepth * 0.055;
    const opacity = depth > 4 ? 0 : 1 - visibleDepth * 0.14;

    return {
      position: "absolute",
      inset: 0,
      transform: `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg) scale(${scale})`,
      transformOrigin: "center 85%",
      opacity,
      zIndex: count - depth,
      transition: "transform 0.65s cubic-bezier(0.22,1,0.36,1), opacity 0.5s ease",
      pointerEvents: depth === 0 ? "auto" : "none",
      borderRadius: 20,
      overflow: "hidden",
      boxShadow:
        depth === 0
          ? "0 24px 60px rgba(108,10,159,0.18)"
          : "0 10px 24px rgba(23,11,46,0.14)",
    };
  };

  const arrowStyle = (side) => ({
    position: "absolute",
    [side]: 12,
    top: "50%",
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "rgba(108,10,159,0.7)",
    color: "#fff",
    fontSize: 18,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
    zIndex: count + 1,
    opacity: 0,
    transition: "opacity 0.2s ease, background 0.2s",
  });

  return (
    <div
      className="project-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={next}
      style={{
        position: "relative",
        borderRadius: 20,
        background: bg,
        aspectRatio: "16 / 10",
        cursor: count > 1 ? "pointer" : "default",
        padding: "4% 6%",
      }}
    >
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {images.map((type, i) => (
          <div key={i} style={cardStyle(stackPosition(i))}>
            <CarouselSlide
              src={type}
              accent={accent}
              bg={bg}
              loadSrc={allowLoad}
              loading="eager"
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous screenshot"
            className="carousel-arrow"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            style={arrowStyle("left")}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next screenshot"
            className="carousel-arrow"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            style={arrowStyle("right")}
          >
            ›
          </button>

          <div
            style={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 6,
              zIndex: count + 1,
            }}
          >
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to screenshot ${i + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  goTo(i);
                }}
                style={{
                  width: i === index ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  border: "none",
                  cursor: "pointer",
                  background: i === index ? "#fff" : "rgba(255,255,255,0.55)",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* =====================================================================
   PROJECT ROW
===================================================================== */

const ProjectRow = ({ project, reverse, onView, eagerFirst = false }) => {
  const [ref, inView] = useInView(0.15);
  const [btnHov, setBtnHov] = useState(false);
  const allowLoad = eagerFirst || inView;

  return (
    <div ref={ref} className={`project-row${reverse ? " reverse" : ""}`}>
      <div
        className="project-image"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateX(0)" : `translateX(${reverse ? 36 : -36}px)`,
          transition: "opacity 0.8s ease, transform 0.8s ease",
        }}
      >
        <ProjectCarousel
          images={project.images}
          accent={project.accentColor}
          bg={project.bgColor}
          allowLoad={allowLoad}
        />
      </div>

      <div
        className="project-details"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateX(0)" : `translateX(${reverse ? -36 : 36}px)`,
          transition: "opacity 0.8s ease 0.12s, transform 0.8s ease 0.12s",
        }}
      >
        <div style={{ fontFamily: FF.body, fontSize: 12, fontWeight: 700, color: project.accentColor, letterSpacing: "0.06em", marginBottom: 12, textTransform: "uppercase" }}>
          {project.index} / {project.eyebrowLabel}
        </div>

        <h3
          style={{
            fontFamily: FF.display,
            fontSize: "clamp(1.5rem, 2.6vw, 2rem)",
            fontWeight: 800,
            color: C.bright,
            margin: "0 0 6px",
            lineHeight: 1.2,
            letterSpacing: "-0.5px",
          }}
        >
          {project.sector}
        </h3>

        <div style={{ fontFamily: FF.body, fontSize: 15, fontWeight: 600, color: C.subText, marginBottom: 16 }}>{project.productName}</div>

        <p style={{ fontFamily: FF.body, fontSize: 15, color: C.subText, lineHeight: 1.7, margin: "0 0 22px", maxWidth: 460 }}>
          {project.tagline}
        </p>

        <ul
          className="project-features"
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0 0 28px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px 16px",
          }}
        >
          {project.features.map((f) => (
            <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontFamily: FF.body, fontSize: 13.5, color: C.text, fontWeight: 500 }}>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                <circle cx="9" cy="9" r="9" fill={project.accentColor} opacity="0.15" />
                <path d="M5.5 9l2.5 2.5L12.5 7" stroke={project.accentColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {f}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => onView(project.slug)}
          onMouseEnter={() => setBtnHov(true)}
          onMouseLeave={() => setBtnHov(false)}
          className="view-product-btn"
          style={{
            padding: "12px 26px",
            borderRadius: 10,
            fontFamily: FF.body,
            fontWeight: 700,
            fontSize: 14.5,
            color: "#fff",
            border: "none",
            background: "#6C0A9F",
            cursor: "pointer",
            transform: btnHov ? "translateY(-2px)" : "none",
            boxShadow: btnHov ? "0 12px 28px rgba(108,10,159,0.35)" : "0 4px 14px rgba(108,10,159,0.2)",
            transition: "all 0.25s ease",
          }}
        >
          View Product →
        </button>
      </div>
    </div>
  );
};

/* =====================================================================
   WHY-CHOOSE-US CARD
===================================================================== */

const WhyCard = ({ item }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        textAlign: "center",
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? "#6C0A9F" : C.border}`,
        borderRadius: 14,
        padding: "28px 20px",
        transition: "all 0.25s ease",
        transform: hov ? "translateY(-4px)" : "none",
        boxShadow: hov ? "0 16px 36px rgba(108,10,159,0.15)" : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: C.raised,
          border: `1.5px solid ${hov ? item.accent : "transparent"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
          transition: "all 0.2s ease",
        }}
      >
        {item.icon}
      </div>
      <h4
        style={{
          fontFamily: FF.display,
          fontSize: 16,
          fontWeight: 700,
          color: hov ? "#6C0A9F" : C.bright,
          margin: "0 0 8px",
          transition: "color 0.2s",
        }}
      >
        {item.title}
      </h4>
      <p style={{ fontFamily: FF.body, fontSize: 13, color: C.subText, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
    </div>
  );
};

/* =====================================================================
   GLOBAL STAT CARD
===================================================================== */

const GlobalStatCard = ({ s }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "18px 20px",
        borderRadius: 14,
        background: hov ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${hov ? "#D8B4FE" : "rgba(255,255,255,0.12)"}`,
        transform: hov ? "translateY(-3px)" : "none",
        transition: "all 0.2s ease",
      }}
    >
      <div
        style={{
          width: 50,
          height: 50,
          borderRadius: 12,
          background: s.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {s.icon}
      </div>
      <AnimatedStat
        value={s.value}
        decimals={s.decimals || 0}
        suffix={s.suffix || ""}
        label={s.label}
        color="#fff"
        size={24}
        labelColor="#E0D2F0"
      />
    </div>
  );
};

/* Related Product Pill Button */
const RelatedProductButton = ({ project, onView, floating = false }) => {
  const [hov, setHov] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onView(project.slug)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: floating ? "8px 16px" : "10px 20px",
        borderRadius: 30,
        border: `1.5px solid ${hov ? "#6C0A9F" : C.border}`,
        background: hov ? "#6C0A9F" : C.surface,
        color: hov ? "#fff" : C.text,
        fontFamily: FF.body,
        fontWeight: 600,
        fontSize: floating ? 12.5 : 14,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        transform: !floating && hov ? "translateY(-2px)" : "none",
        boxShadow: hov ? "0 10px 24px rgba(108,10,159,0.25)" : (floating ? "0 4px 14px rgba(18,6,31,0.18)" : "none"),
      }}
    >
      {project.productName}
      <span>→</span>
    </button>
  );
};

/* Related Products Row */
const RelatedProductsSection = ({ currentSlug, onView }) => {
  const others = ProductsData.filter((p) => p.slug !== currentSlug);
  if (others.length === 0) return null;

  return (
    <section style={{ background: C.bg, padding: "56px 24px 32px" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <Label text="More From Sagveek" />
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 14,
          maxWidth: 900,
          margin: "0 auto",
        }}
      >
        {others.map((project) => (
          <RelatedProductButton key={project.slug} project={project} onView={onView} />
        ))}
      </div>
    </section>
  );
};

/* =====================================================================
   MAIN LAYOUT
===================================================================== */

const ProductsIndexLayout = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [backHov, setBackHov] = useState(false);
  const navigate = useNavigate();
  const { slug } = useParams();

  const handleViewProduct = (productSlug) => {
    navigate(`/products/${productSlug}`);
  };

  const handleBackToProducts = () => {
    navigate("/products");
  };

  const filteredProducts = ProductsData.filter(
    (product) =>
      product.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sector.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedProduct = slug ? ProductsData.find((p) => p.slug === slug) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  /* ---------- DETAIL VIEW ---------- */
  if (slug) {
    if (!selectedProduct || !selectedProduct.DetailComponent) {
      return (
        <div
          style={{
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "80px 24px",
            fontFamily: FF.body,
            background: C.bg,
          }}
        >
          <div style={{ fontFamily: FF.body, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: "#6C0A9F", marginBottom: 12 }}>
            PRODUCT NOT AVAILABLE
          </div>
          <h1
            style={{
              fontFamily: FF.display,
              fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
              fontWeight: 800,
              color: C.bright,
              margin: "0 0 12px",
            }}
          >
            We couldn't find that product
          </h1>
          <p style={{ color: C.subText, fontSize: 15, maxWidth: 460, margin: "0 0 28px", lineHeight: 1.7 }}>
            "<strong>{slug}</strong>" doesn't match a product page yet. It may still be in progress, or the link may
            be out of date.
          </p>
          <button
            type="button"
            onClick={handleBackToProducts}
            style={{
              padding: "12px 28px",
              borderRadius: 10,
              fontFamily: FF.body,
              fontWeight: 700,
              fontSize: 14.5,
              color: "#fff",
              border: "none",
              background: "#6C0A9F",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#52067A")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#6C0A9F")}
          >
            ← Back to Products
          </button>

          <div style={{ width: "100%", marginTop: 48 }}>
            <RelatedProductsSection currentSlug={slug} onView={handleViewProduct} />
          </div>
        </div>
      );
    }

    const { DetailComponent } = selectedProduct;

    return (
      <div style={{ background: C.bg }}>
        <button
          type="button"
          onClick={handleBackToProducts}
          onMouseEnter={() => setBackHov(true)}
          onMouseLeave={() => setBackHov(false)}
          style={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 18px",
            borderRadius: 30,
            border: "none",
            background: backHov ? "#52067A" : "#6C0A9F",
            color: "#fff",
            fontFamily: FF.body,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(108,10,159,0.45)",
            backdropFilter: "blur(6px)",
            transition: "all 0.25s ease",
          }}
        >
          ← Back to Products
        </button>

        <DetailComponent />
      </div>
    );
  }

  /* ---------- INDEX VIEW ---------- */
  return (
    <div style={{ fontFamily: FF.body, background: C.bg, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

        .project-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 56px;
          align-items: center;
          padding: 60px 24px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .project-row .project-image { order: 1; }
        .project-row .project-details { order: 2; }
        .project-row.reverse .project-image { order: 2; }
        .project-row.reverse .project-details { order: 1; }

        .project-carousel:hover .carousel-arrow { opacity: 1; }

        @media (max-width: 860px) {
          .project-row {
            grid-template-columns: 1fr;
            gap: 28px;
            padding: 40px 20px;
          }
          .project-row .project-image,
          .project-row.reverse .project-image { order: 1; }
          .project-row .project-details,
          .project-row.reverse .project-details { order: 2; }
        }

        /* Feature list collapses to a single column on narrow phones */
        @media (max-width: 420px) {
          .project-features { grid-template-columns: 1fr !important; }
        }

        /* Carousel arrows are hover-only on desktop; show them always on touch/small screens
           so people know they can navigate without relying on hover */
        @media (max-width: 860px) {
          .carousel-arrow { opacity: 0.85 !important; width: 30px !important; height: 30px !important; font-size: 15px !important; }
        }

        /* Hero search bar stacks on narrow screens */
        .hero-search { display: flex; align-items: center; }
        .hero-search input { min-width: 0; }
        @media (max-width: 560px) {
          .hero-search {
            flex-direction: column;
            border-radius: 20px !important;
            padding: 14px !important;
            gap: 10px;
          }
          .hero-search input { width: 100%; padding: 6px 4px; }
          .hero-search button { width: 100%; }
        }

        /* Tighten section padding on small screens */
        @media (max-width: 640px) {
          .products-hero { padding: 44px 18px 64px !important; }
          .products-suite-section { padding: 36px 0 12px !important; }
          .products-stats-section { padding: 36px 18px !important; }
          .products-why-section { padding: 48px 18px 60px !important; }
        }
      `}</style>

      {/* ── Hero ── */}
      <section
        className="products-hero"
        style={{
          background: `linear-gradient(135deg, ${C.tealD} 0%, ${C.teal} 55%, ${C.tealD} 100%)`,
          padding: "60px 24px 80px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "20%", left: "8%", width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(108,10,159,0.3) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "15%", right: "10%", width: 100, height: 100, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.3) 0%, transparent 70%)" }} />

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 20,
            padding: "6px 16px",
            marginBottom: 20,
            color: "#EAF0EE",
            fontFamily: FF.body,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            backdropFilter: "blur(8px)",
          }}
        >
          <span style={{ color: "#D8B4FE" }}>✦</span> OUR PRODUCTS
        </div>

        <h1
          style={{
            fontFamily: FF.display,
            fontSize: "clamp(2rem, 5vw, 3.25rem)",
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.15,
            margin: "0 0 12px",
            letterSpacing: "-1px",
          }}
        >
          Smart Solutions for
          <br />
          <span style={{ color: "#6C0A9F" }}>Modern Businesses</span>
        </h1>

        <p style={{ fontFamily: FF.body, color: "#D7DEE9", fontSize: 16, maxWidth: 520, margin: "0 auto 36px", lineHeight: 1.7 }}>
          Explore our powerful software solutions designed to streamline operations, improve efficiency, and drive growth.
        </p>

        <div
          className="hero-search"
          style={{
            maxWidth: 680,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 50,
            padding: "6px 6px 6px 20px",
            boxShadow: "0 8px 32px rgba(18,6,31,0.4)",
          }}
        >
          <span style={{ color: C.dimText, fontSize: 18, display: "flex", alignItems: "center", marginRight: 10 }}>🔍</span>
          <input
            type="text"
            placeholder="Search products, solutions or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search products"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontFamily: FF.body,
              fontSize: 15,
              color: C.text,
              background: "transparent",
            }}
          />
          <button
            type="button"
            style={{
              background: "#6C0A9F",
              color: "#fff",
              fontFamily: FF.body,
              fontWeight: 700,
              fontSize: 15,
              border: "none",
              borderRadius: 40,
              padding: "12px 28px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#52067A")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#6C0A9F")}
          >
            Search →
          </button>
        </div>
      </section>

      {/* ── Wave divider ── */}
      <div style={{ marginTop: -2, lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" style={{ width: "100%", display: "block" }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,0 L0,0 Z" fill={C.tealD} />
        </svg>
      </div>

      {/* ── Product Suite · 5 Platforms ── */}
      <section className="products-suite-section" style={{ background: C.surface, padding: "48px 0 20px" }}>
        <div style={{ textAlign: "center", padding: "0 24px 8px" }}>
          <Label text="Product Suite · 5 Platforms" />
          <h2
            style={{
              fontFamily: FF.display,
              fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
              fontWeight: 800,
              color: C.bright,
              margin: "0 0 12px",
              letterSpacing: "-0.5px",
            }}
          >
            Software Built Around Your Workflow
          </h2>
          <p style={{ fontFamily: FF.body, color: C.subText, fontSize: 14.5, maxWidth: 560, margin: "0 auto" }}>
            A closer look at each platform — hover a preview to pause it, or use the arrows and dots to browse.
          </p>
        </div>

        {filteredProducts.length === 0 ? (
          <p style={{ textAlign: "center", color: C.dimText, padding: "60px 24px" }}>No products found.</p>
        ) : (
          filteredProducts.map((project, i) => (
            <ProjectRow
              key={project.slug}
              project={project}
              reverse={i % 2 === 1}
              onView={handleViewProduct}
              eagerFirst={i === 0}
            />
          ))
        )}
      </section>

      {/* ── Stats Bar ── */}
      <section
        className="products-stats-section"
        style={{
          background: `linear-gradient(135deg, ${C.tealD} 0%, ${C.teal} 60%, ${C.tealD} 100%)`,
          padding: "48px 24px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 20,
            maxWidth: 960,
            margin: "0 auto",
          }}
        >
          {globalStats.map((s) => (
            <GlobalStatCard key={s.label} s={s} />
          ))}
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="products-why-section" style={{ padding: "64px 24px 80px", background: C.surface }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <Label text="Why Choose Us?" />
          <h2
            style={{
              fontFamily: FF.display,
              fontSize: "clamp(1.6rem, 3vw, 2.25rem)",
              fontWeight: 800,
              color: C.bright,
              margin: 0,
              letterSpacing: "-0.5px",
            }}
          >
            Built for Performance. Designed for You.
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 20,
            maxWidth: 1080,
            margin: "0 auto",
          }}
        >
          {whyChooseUs.map((item) => (
            <WhyCard key={item.title} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default ProductsIndexLayout;